// useShortShareLink.js
import { useRef, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import fetchShortLink from '../action/fetchShortLink.js';

import getShortLinkData from '../selector/getShortLinkData.js';
import getRemoteConfigData from '../selector/getRemoteConfigData.js';
import getMeData from '../selector/getMeData.js';
import getNetworkingData from '../selector/getNetworkingData.js';

import trackMixpanel, {
  getViewId,
  ButtonId,
  EventTypes,
} from '../resource/mixpanel.js';
import getShareLink from '../resource/getShareLink.js';
import {
  ANDROID_MINIMUM_VERSION,
  SWAG_ANDROID_PACKAGE_NAME,
} from '../RemoteConfigKeys.js';

export const ShareType = {
  STORY: 'story',
  SHORT: 'short',
  LIVESTREAM: 'livestream',
  POST: 'post',
};

/**
 * A hook to lazy fetch short share link.
 * You may want to write your processing code inside callback function.
 *
 * @example
 *
 * import useShortShareLink from 'useShortShareLink'
 *
 * const [isFetchingShortShareLink, fetchShortShareLink] = useShortShareLink({
 *  targetId: 'sampleId',
 *  shareType: 'story',
 *  onShortShareLinkFetched: shortShareLink => {
 *    copyTextToClipboard(shortShareLink);
 *  },
 * });
 *
 * @param {Object} param
 * @param {String} param.targetId - A target id to be shared.
 * @param {('story'|'short'|'livestream'|'post')} param.shareType - The type of media.
 * @param {(shortShareLink: string) => void} param.onShortShareLinkFetched - The callback function that takes a shareShortLink parameter.
 * @return {[Boolean, Function]} Return the array that the first element is `isFetching`, the second element is `fetchShortShareLink` respectively.
 *
 */
const useShortShareLink = ({
  targetId,
  shareType,
  onShortShareLinkFetched,
}) => {
  const dispatch = useDispatch();
  const meId = useSelector(state => getMeData(state, 'id'));
  const utmQueries = useMemo(() => {
    if (Object.values(ShareType).includes(shareType)) {
      return {
        utm_source: 'share',
        utm_content: `${shareType}_${targetId}`,
        utm_campaign: meId ? `userid_${meId}` : '',
      };
    }
    return undefined;
  }, [meId, targetId, shareType]);

  const swagAndroidPackageName = useSelector(state =>
    getRemoteConfigData(state, SWAG_ANDROID_PACKAGE_NAME)
  );
  const androidMinimunVersion = useSelector(state =>
    getRemoteConfigData(state, ANDROID_MINIMUM_VERSION)
  );
  const shareLink =
    shareType === ShareType.LIVESTREAM
      ? getShareLink({
          query: {
            link: typeof window !== 'undefined' ? window?.location?.href : null,
            afl: typeof window !== 'undefined' ? window?.location?.href : null,
            apn: swagAndroidPackageName,
            amv: androidMinimunVersion,
          },
        })
      : getShareLink();
  const shortShareLink = useSelector(state =>
    getShortLinkData(state, shareLink)
  );
  const shortShareLinkRef = useRef();
  shortShareLinkRef.current = shortShareLink;

  const shouldTrackMessageId = !![
    ShareType.SHORT,
    ShareType.STORY,
    ShareType.POST,
  ].includes(shareType);

  const isFetchingShortShareLink = !!useSelector(state =>
    getNetworkingData(state, ['shortLink', shareLink], 'isFetching')
  );

  const fetchShortShareLink = useCallback(async () => {
    if (shareLink && !shortShareLink) {
      await dispatch(
        fetchShortLink({
          location: shareLink,
          utmQueries,
        })
      );
    }

    trackMixpanel({
      type: EventTypes.USER_CLICKED,
      payload: {
        viewId: getViewId({ pathname: window.location.pathname }),
        buttonId: ButtonId.All.ButtonShare,
        ...(shouldTrackMessageId && { messageId: targetId }),
      },
    });

    onShortShareLinkFetched(
      shortShareLinkRef.current || shareLink || location.href
    );
  }, [
    dispatch,
    onShortShareLinkFetched,
    shareLink,
    shortShareLink,
    shouldTrackMessageId,
    targetId,
    utmQueries,
  ]);

  return [isFetchingShortShareLink, fetchShortShareLink];
};

export default useShortShareLink;
